import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import TableCard from "../../components/ui-components/TableCard";
import FlexTable from "../../components/ui-components/FlexTable";
import EmptyState from "../../components/ui-components/EmptyState";
import Badge from "../../components/ui-components/Badge";
import StatusPill from "../Budgets/components/StatusPill";
import { toast } from "../../components/ui-components/Toast";
import { useHasCapability } from "../../authz/useCapabilities";
import { CAPS } from "../../authz/capabilities";
import { listUsers } from "../users/api";
import RoleCard, { accessLevelBadge } from "./RoleCard";
import { HeaderPortal } from "../../components/ui-components/HeaderSlot.jsx";
import CreateRoleMenu from "./CreateRoleMenu";
import TemplatePickerSheet from "./TemplatePickerSheet";
import RoleDetailModal from "./RoleDetailModal";
import TemplateDiffModal from "./TemplateDiff";
import StarterSetPanel from "./StarterSetPanel";
import PlatformAssignmentsModal from "./PlatformAssignmentsModal";
import { useRoles, useCapabilityCatalog, groupRolesByType, useTransitionRoleMutation, useTemplateUpdates } from "./api";
import { friendlyTransitionError, reviewStatusLabel, fmtDate } from "./roleFormUtils";

// Three categories of real objects. Templates deliberately absent — they
// grant no access and can't be assigned, so a peer tab made seven inert
// objects read as live roles. They live in TemplatePickerSheet now, reached
// from CreateRoleMenu at the point an admin is choosing a starting point.
const TABS = [
  { key: "organisation", label: "Organisation" },
  { key: "platform", label: "Platform" },
  { key: "archived", label: "Archived" },
];

// Lifecycle transition -> user-facing success toast + the friendly-error
// action label (see roleFormUtils.friendlyTransitionError).
const TRANSITION_COPY = {
  active: { success: "Role activated", action: "activate" },
  pending_approval: { success: "Submitted for approval", action: "submit for approval" },
  retired: { success: "Role retired", action: "retire" },
};

const actionBtnCls =
  "px-2 py-1 text-xs rounded-ctrl transition-colors border-0 bg-transparent box-border cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center shrink-0";

// What the three roles cards show while their query is in flight. TableCard's
// built-in `isLoading` renders a spinner; these three surfaces opt out of it
// and reserve the same height instead, because each already says "Loading…" in
// its subtitle — the spinner restated that in motion, and three of them
// animating behind a tab strip read as busier than the wait actually is. The
// height is TableCard's own min-h so the card doesn't collapse and rebound
// when the roles arrive.
function RolesLoadingBody() {
  return <div className="min-h-[240px]" />;
}

// Governed organisation roles table (Sub-project E2 Task 7): real governance
// columns — Role · Based on · Users · Access level · Status · Review — plus
// lifecycle affordances gated on `canManage`, per row status: draft ->
// Activate/Submit for approval, pending_approval -> Activate, active ->
// Retire. A row click (or clicking its name) opens the read-only governance
// summary (see RoleDetailModal). Retired roles never reach this table —
// api.groupRolesByType routes them into the Archived bucket at the source.
function OrganisationRolesTable({
  roles,
  roleNameByCode,
  templateUpdatesByRoleId = new Map(),
  onCompareTemplate,
  canManage,
  isLoading,
  error,
  onRetry,
  onStartFromTemplate,
  onViewDetail,
  onActivate,
  onSubmitForApproval,
  onRetire,
  pendingRoleId,
}) {
  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      {
        id: "name",
        Header: () => <span>Role</span>,
        Cell: ({ row }) => {
          const upd = templateUpdatesByRoleId.get(row.original.id);
          return (
            <div className="min-w-0">
              <div className="truncate font-medium text-text">{row.original.name}</div>
              {row.original.purpose && <div className="truncate text-xs text-text-2">{row.original.purpose}</div>}
              {upd && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCompareTemplate(row.original);
                  }}
                  className="mt-1 inline-flex items-center gap-1 rounded-full border-0 box-border bg-primary-soft px-2 py-0.5 text-[11px] font-medium text-primary hover:bg-primary-soft-strong cursor-pointer"
                >
                  Newer template · +{upd.addedCount}/−{upd.removedCount}
                </button>
              )}
            </div>
          );
        },
        flex: 1,
        minWidth: 200,
      },
      {
        id: "basedOn",
        Header: () => <span>Based on</span>,
        Cell: ({ row }) => (
          <span className="text-xs text-text-2">
            {row.original.cloned_from ? roleNameByCode.get(row.original.cloned_from) || row.original.cloned_from : "—"}
          </span>
        ),
        width: 160,
        minWidth: 120,
      },
      {
        id: "users",
        Header: () => <span>Users</span>,
        align: "center",
        Cell: ({ row }) => <span className="tabular-nums text-sm text-text">{row.original.assigned_user_count ?? 0}</span>,
        width: 80,
        minWidth: 60,
        maxWidth: 100,
      },
      {
        id: "access",
        Header: () => <span>Access level</span>,
        Cell: ({ row }) => {
          const b = accessLevelBadge(row.original.risk);
          return <Badge status={b.status} label={b.label} size="sm" />;
        },
        width: 130,
        minWidth: 110,
      },
      {
        id: "status",
        Header: () => <span>Status</span>,
        Cell: ({ row }) => <StatusPill status={row.original.status} />,
        width: 150,
        minWidth: 130,
      },
      {
        id: "review",
        Header: () => <span>Review</span>,
        Cell: ({ row }) => {
          const label = reviewStatusLabel(row.original.next_review_at);
          return (
            <span className={`text-xs ${label === "Overdue" ? "font-semibold text-danger" : "text-text-2"}`}>
              {label}
            </span>
          );
        },
        width: 120,
        minWidth: 100,
      },
      {
        id: "action",
        align: "right",
        Header: () => null,
        Cell: ({ row }) => {
          if (!canManage) return null;
          const r = row.original;
          const busy = pendingRoleId === r.id;
          const stop = (fn) => (e) => {
            e.stopPropagation();
            fn(r);
          };
          return (
            <div className="flex flex-wrap items-center justify-end gap-1">
              {r.status === "draft" && (
                <>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={stop(onSubmitForApproval)}
                    className={`${actionBtnCls} text-text-2 hover:bg-surface-2`}
                  >
                    Submit for approval
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={stop(onActivate)}
                    className={`${actionBtnCls} text-primary hover:bg-primary-soft`}
                  >
                    Activate
                  </button>
                </>
              )}
              {r.status === "pending_approval" && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={stop(onActivate)}
                  className={`${actionBtnCls} text-primary hover:bg-primary-soft`}
                >
                  Activate
                </button>
              )}
              {r.status === "active" && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={stop(onRetire)}
                  className={`${actionBtnCls} text-danger hover:bg-danger-soft`}
                >
                  Retire
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/settings/roles/${r.id}`);
                }}
                className={`${actionBtnCls} text-primary hover:bg-primary-soft`}
              >
                Edit
              </button>
            </div>
          );
        },
        width: 330,
        minWidth: 260,
      },
    ],
    [canManage, roleNameByCode, templateUpdatesByRoleId, onCompareTemplate, navigate, onActivate, onSubmitForApproval, onRetire, pendingRoleId]
  );

  return (
    <TableCard
      title="Organisation roles"
      subtitle={isLoading ? "Loading…" : `${roles.length} role${roles.length === 1 ? "" : "s"} authored by this tenant`}
      error={error}
      errorMessage="Error loading roles"
      onRetry={onRetry}
    >
      {isLoading ? (
        <RolesLoadingBody />
      ) : roles.length === 0 ? (
        <EmptyState
          icon={icon({ name: "shield-halved", style: "light" })}
          title="No organisation roles yet"
          message="Use a role template, or start from scratch, to create your tenant's first organisation role."
          action={
            canManage
              ? { label: "Start from a template", onClick: onStartFromTemplate, variant: "primary" }
              : undefined
          }
        />
      ) : (
        <FlexTable
          columns={columns}
          data={roles}
          getRowId={(r) => String(r.id)}
          rowAriaLabel={(r) => r.name}
          onRowClick={(_id, original) => onViewDetail(original)}
        />
      )}
    </TableCard>
  );
}

// Platform roles tab: locked, directly-assignable roles fixed by the
// platform (tenant_owner, security_admin) — no clone affordance.
function PlatformRolesGrid({ roles, isLoading, error, onRetry, onManageAssignments }) {
  return (
    <TableCard
      title="Platform roles"
      subtitle={isLoading ? "Loading…" : `${roles.length} role${roles.length === 1 ? "" : "s"} fixed by the platform — assign directly, can't be cloned`}
      error={error}
      errorMessage="Error loading roles"
      onRetry={onRetry}
    >
      {isLoading ? (
        <RolesLoadingBody />
      ) : roles.length === 0 ? (
        <EmptyState
          icon={icon({ name: "lock", family: "sharp", style: "light" })}
          title="No platform roles"
          message="Platform roles will appear here."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {roles.map((role) => (
            <RoleCard
              key={role.code}
              role={role}
              variant="platform"
              onManageAssignments={onManageAssignments}
            />
          ))}
        </div>
      )}
    </TableCard>
  );
}

// Archived tab: read-only list of retired organisation roles (Sub-project E2
// Task 7 — retirement is now modeled via the role lifecycle; api.js's
// groupRolesByType routes status='retired' organisation roles here). No
// lifecycle actions surface from this tab; a row click still opens the
// governance summary so an admin can see why/when it was retired-adjacent
// context (owner, review cadence) without reactivating it from here.
function ArchivedRolesTable({ roles, roleNameByCode, isLoading, error, onRetry, onViewDetail }) {
  const columns = useMemo(
    () => [
      {
        id: "name",
        Header: () => <span>Role</span>,
        Cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate font-medium text-text">{row.original.name}</div>
            {row.original.purpose && <div className="truncate text-xs text-text-2">{row.original.purpose}</div>}
          </div>
        ),
        flex: 1,
        minWidth: 220,
      },
      {
        id: "basedOn",
        Header: () => <span>Based on</span>,
        Cell: ({ row }) => (
          <span className="text-xs text-text-2">
            {row.original.cloned_from ? roleNameByCode.get(row.original.cloned_from) || row.original.cloned_from : "—"}
          </span>
        ),
        width: 200,
        minWidth: 140,
      },
      {
        id: "users",
        Header: () => <span>Users</span>,
        align: "center",
        Cell: ({ row }) => <span className="tabular-nums text-sm text-text">{row.original.assigned_user_count ?? 0}</span>,
        width: 90,
        minWidth: 70,
        maxWidth: 110,
      },
      {
        id: "access",
        Header: () => <span>Access level</span>,
        Cell: ({ row }) => {
          const b = accessLevelBadge(row.original.risk);
          return <Badge status={b.status} label={b.label} size="sm" />;
        },
        width: 140,
        minWidth: 120,
      },
      {
        id: "status",
        Header: () => <span>Status</span>,
        Cell: () => <StatusPill status="retired" />,
        width: 130,
        minWidth: 110,
      },
      {
        id: "retiredAt",
        Header: () => <span>Retired</span>,
        Cell: ({ row }) => <span className="text-xs text-text-2">{fmtDate(row.original.retired_at)}</span>,
        width: 130,
        minWidth: 110,
      },
    ],
    [roleNameByCode]
  );

  return (
    <TableCard
      title="Archived roles"
      subtitle={isLoading ? "Loading…" : `${roles.length} retired role${roles.length === 1 ? "" : "s"}`}
      error={error}
      errorMessage="Error loading roles"
      onRetry={onRetry}
    >
      {isLoading ? (
        <RolesLoadingBody />
      ) : roles.length === 0 ? (
        <EmptyState
          icon={icon({ name: "box-archive", style: "light" })}
          title="No archived roles"
          message="Roles retired from the Organisation tab will appear here."
        />
      ) : (
        <FlexTable
          columns={columns}
          data={roles}
          getRowId={(r) => String(r.id)}
          rowAriaLabel={(r) => r.name}
          onRowClick={(_id, original) => onViewDetail(original)}
        />
      )}
    </TableCard>
  );
}

// "Review template updates" section (Sub-project E3, Task 4): the tenant-wide
// feed of organisation roles whose cloning template has drifted, grouped by
// template code so an admin reviewing "what changed in the Finance Manager
// template" sees every affected role together. Rendered above the tabs
// (regardless of which one is active) since the feed spans every
// organisation role, not just whichever tab happens to be open; renders
// nothing when there's nothing behind.
function TemplateUpdatesReview({ updates = [], onCompare }) {
  const grouped = useMemo(() => {
    const byTemplate = new Map();
    for (const u of updates) {
      if (!byTemplate.has(u.templateCode)) byTemplate.set(u.templateCode, []);
      byTemplate.get(u.templateCode).push(u);
    }
    return Array.from(byTemplate.entries());
  }, [updates]);

  if (updates.length === 0) return null;

  return (
    <TableCard
      title="Review template updates"
      subtitle={`${updates.length} organisation role${updates.length === 1 ? "" : "s"} behind their cloning template`}
    >
      <div className="space-y-4" data-testid="template-updates-review">
        {grouped.map(([templateCode, rows]) => (
          <div key={templateCode}>
            <div className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-text-3 mb-1.5">{templateCode}</div>
            <div className="divide-y divide-border">
              {rows.map((u) => (
                <div key={u.roleId} className="flex items-center justify-between gap-3 py-2">
                  <span className="text-sm text-text">{u.roleName}</span>
                  <button
                    type="button"
                    onClick={() => onCompare({ id: u.roleId, name: u.roleName })}
                    className="inline-flex shrink-0 items-center gap-1 rounded-full border-0 box-border bg-primary-soft px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary-soft-strong cursor-pointer"
                  >
                    +{u.addedCount}/−{u.removedCount} · Compare
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </TableCard>
  );
}

// Roles & Access catalog: the tenant's role catalog split into three tabs by
// `role.type` — Organisation (tenant-authored, assignable roles; default
// tab), Platform (fixed, directly-assignable platform roles that can't be
// cloned), and Archived (retired organisation roles — Sub-project E2 Task 7).
// Templates (system roles that seed new organisation roles via cloning, not
// directly assignable) are deliberately not a tab — see TABS above; they
// surface from CreateRoleMenu's page-level Create role action, via
// TemplatePickerSheet. Scope-only roles hidden by default (the legacy
// retired "admin" role) surface behind the "Show hidden roles" toggle,
// applied across all tabs.
export default function RolesCatalogPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [showHidden, setShowHidden] = useState(false);
  // Defaults to "organisation"; honors a `?tab=` deep link (e.g. the access
  // review Platform panel's "Review now" link into the Platform tab) when it
  // names one of the three known tabs. A stale ?tab=template deep link (from
  // before Templates stopped being a tab) falls back to "organisation" the
  // same as any other unrecognized value.
  const requestedTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    TABS.some((t) => t.key === requestedTab) ? requestedTab : "organisation"
  );
  const [detailRole, setDetailRole] = useState(null);
  const [compareRole, setCompareRole] = useState(null);
  const [picker, setPicker] = useState({ open: false, scope: "recommended" });
  const [starterSetOpen, setStarterSetOpen] = useState(false);
  const [managingPlatformRole, setManagingPlatformRole] = useState(null);
  const rolesQ = useRoles({ includeHidden: showHidden });
  const capsQ = useCapabilityCatalog();
  const templateUpdatesQ = useTemplateUpdates();
  const canManage = useHasCapability(CAPS.ROLES_ASSIGN);

  const roles = useMemo(() => rolesQ.data || [], [rolesQ.data]);
  const capabilityCatalog = capsQ.data || [];
  const isLoading = rolesQ.isLoading || capsQ.isLoading;
  const error = rolesQ.error || capsQ.error;

  const grouped = useMemo(() => groupRolesByType(roles), [roles]);

  // Resolves an organisation role's `cloned_from` (a role code) to its
  // display name — the clone source is often a template/platform role that
  // isn't in the organisation bucket itself.
  const roleNameByCode = useMemo(() => {
    const m = new Map();
    for (const r of roles) m.set(r.code, r.name);
    return m;
  }, [roles]);

  // Tenant-wide "behind" feed keyed by roleId — the cheap source for the
  // Organisation table's "Newer template" chip and the Review template
  // updates section below, instead of a per-row template-diff call.
  const templateUpdates = useMemo(() => templateUpdatesQ.data || [], [templateUpdatesQ.data]);
  const templateUpdatesByRoleId = useMemo(() => {
    const m = new Map();
    for (const u of templateUpdates) m.set(u.roleId, u);
    return m;
  }, [templateUpdates]);

  // Owner lookup for the role-detail summary — fetched lazily (only once a
  // detail view is actually opened) since it's a whole-tenant users fetch
  // just to resolve one id -> display name.
  const usersQ = useQuery(["users-picker-for-role-owner"], () => listUsers({ limit: 200 }), {
    enabled: !!detailRole,
    staleTime: 5 * 60 * 1000,
  });
  const ownerNameById = useMemo(() => {
    const raw = usersQ.data?.users?.items || [];
    const m = new Map();
    for (const u of raw) {
      const id = String(u.id ?? u.ID ?? "");
      m.set(id, [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || id);
    }
    return m;
  }, [usersQ.data]);

  const invalidateRoles = () => queryClient.invalidateQueries({ queryKey: ["roles"] });

  // Drives the Organisation table's lifecycle affordances (Activate / Submit
  // for approval / Retire) via the shared useTransitionRoleMutation (api.js —
  // also available to any other future caller, so there's a single wrapper
  // around POST /roles/{id}/transition instead of a page-local one). A 409
  // (LIFECYCLE_CONFLICT / ROLE_IN_USE / ROLE_CONTROL_CHECK_FAILED /
  // INVALID_TRANSITION) surfaces the server's message via toast rather than
  // silently failing; success invalidates the catalog so the row's new
  // status (and, for a retirement, its move to the Archived tab) shows
  // immediately.
  const transitionMutation = useTransitionRoleMutation({
    onSuccess: (_data, vars) => {
      invalidateRoles();
      toast.success(TRANSITION_COPY[vars.to]?.success || "Role updated");
    },
    onError: (err, vars) => {
      toast.error(friendlyTransitionError(err, TRANSITION_COPY[vars.to]?.action || "update"));
    },
  });
  const pendingRoleId = transitionMutation.isPending ? transitionMutation.variables?.id : null;

  const handleRetry = () => {
    rolesQ.refetch();
    capsQ.refetch();
  };

  const handleNewOrganisationRole = () => {
    navigate("/settings/roles/new");
  };

  // Shared by the picker's detail footer and RoleCard's "Use this template" —
  // same clone-to-new-organisation-role navigation either way.
  const handleCreateFromTemplate = (role) => {
    setPicker((p) => ({ ...p, open: false }));
    navigate("/settings/roles/new", { state: { cloneFrom: role.code } });
  };

  const counts = {
    organisation: grouped.organisation.length,
    platform: grouped.platform.length,
    archived: grouped.archived.length,
  };

  return (
    <>
      <HeaderPortal slot="actions">
        <CreateRoleMenu
          canManage={canManage}
          onPickRecommended={() => setPicker({ open: true, scope: "recommended" })}
          onPickTemplate={() => setPicker({ open: true, scope: "all" })}
          onCustom={handleNewOrganisationRole}
          onStarterSet={() => setStarterSetOpen(true)}
        />
      </HeaderPortal>

      <AdministrationHeader title="Roles & Access" showPeriodSelector={false} />
      <div className="w-full p-8 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div role="tablist" aria-label="Role catalog sections" className="inline-flex items-center rounded-seg border border-card-bd p-0.5">
            {TABS.map((tab) => {
              const selected = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setActiveTab(tab.key)}
                  className={`rounded-seg px-3 py-1.5 text-[13px] transition-colors ${
                    selected ? "bg-primary font-semibold text-white" : "text-text-2 hover:text-text"
                  }`}
                >
                  {tab.label} ({counts[tab.key]})
                </button>
              );
            })}
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              id="roles-show-hidden"
              type="checkbox"
              aria-label="Show hidden roles"
              checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
              className="rounded border-border"
            />
            <span className="text-xs font-medium text-text-2">Show hidden roles</span>
          </label>
        </div>

        <TemplateUpdatesReview updates={templateUpdates} onCompare={setCompareRole} />

        {activeTab === "organisation" && (
          <OrganisationRolesTable
            roles={grouped.organisation}
            roleNameByCode={roleNameByCode}
            templateUpdatesByRoleId={templateUpdatesByRoleId}
            onCompareTemplate={setCompareRole}
            canManage={canManage}
            isLoading={isLoading}
            error={error}
            onRetry={handleRetry}
            onStartFromTemplate={() => setPicker({ open: true, scope: "recommended" })}
            onViewDetail={setDetailRole}
            onActivate={(r) => transitionMutation.mutate({ id: r.id, to: "active" })}
            onSubmitForApproval={(r) => transitionMutation.mutate({ id: r.id, to: "pending_approval" })}
            onRetire={(r) => transitionMutation.mutate({ id: r.id, to: "retired" })}
            pendingRoleId={pendingRoleId}
          />
        )}

        {activeTab === "platform" && (
          <PlatformRolesGrid
            roles={grouped.platform}
            isLoading={isLoading}
            error={error}
            onRetry={handleRetry}
            onManageAssignments={setManagingPlatformRole}
          />
        )}

        {activeTab === "archived" && (
          <ArchivedRolesTable
            roles={grouped.archived}
            roleNameByCode={roleNameByCode}
            isLoading={isLoading}
            error={error}
            onRetry={handleRetry}
            onViewDetail={setDetailRole}
          />
        )}
      </div>

      <RoleDetailModal
        role={detailRole}
        roleNameByCode={roleNameByCode}
        ownerName={detailRole?.owner_user_id != null ? ownerNameById.get(String(detailRole.owner_user_id)) : null}
        onClose={() => setDetailRole(null)}
        onCompareTemplate={(r) => {
          setDetailRole(null);
          setCompareRole(r);
        }}
      />

      <TemplateDiffModal role={compareRole} onClose={() => setCompareRole(null)} />

      <TemplatePickerSheet
        open={picker.open}
        scope={picker.scope}
        templates={grouped.template}
        capabilityCatalog={capabilityCatalog}
        canManage={canManage}
        isLoading={isLoading}
        onClose={() => setPicker((p) => ({ ...p, open: false }))}
        onCreateRole={handleCreateFromTemplate}
      />

      <StarterSetPanel open={starterSetOpen} onClose={() => setStarterSetOpen(false)} />

      <PlatformAssignmentsModal role={managingPlatformRole} onClose={() => setManagingPlatformRole(null)} />
    </>
  );
}
